import { t } from "@redotech/redo-i18n";
import { Step as ModelStep } from "@redotech/redo-return-core-common/return-flow";
import { StepId as ModelStepId } from "@redotech/redo-return-core-common/return-flow-answers";
import { FlowType } from "@redotech/redo-return-core-common/return-flow-enums";
import { ReturnTypeEnum } from "@redotech/redo-return-core-common/return-type-enum";
import { Team } from "@redotech/redo-team-common/team";
import { FeaturedIconColor } from "@redotech/redo-web/arbiter-components/featured-icon/redo-featured-icon";
import { BlockLayout } from "@redotech/redo-web/flowchart";
import { assertNever } from "@redotech/util/type";
import { Dispatch, ReactElement, ReactNode, SetStateAction } from "react";

export type StepId = string;

export type StepSelectFn = (props: {
  value: StepId | undefined;
  valueChange: (value: StepId | undefined) => void;
  type?: symbol;
  handleIndex?: number;
}) => ReactNode;

export interface StepTypeDetailsProps<T> {
  /** Step selector */
  stepSelect({
    value,
    valueChange,
    type,
    handleIndex,
  }: {
    value: StepId | undefined | null;
    valueChange(value: StepId | undefined): void;
    type?: symbol;
    handleIndex?: number;
  }): ReactNode;
  /** Set new state */
  setState: Dispatch<SetStateAction<T>>;
  /** State */
  state: T;
  /** Flow type */
  flowType: FlowType;
  /** Used by Multiple Conditions and Multiple Actions steps when a child Condition or Action is deleted */
  onDeleteChild?: () => void;
  step: Step;
  allSteps: Map<StepId, Step>;
  setStateWithId: (
    id: StepId,
    passedStep: SetStateAction<Step | undefined>,
    state: any,
  ) => void;
}

export interface StepType<T, M> {
  readonly empty: T;
  readonly title: string;
  readonly information?: string;
  readonly ignore?: boolean;
  defaultInitialStateFn?(steps: Map<StepId, Step>): T;
  displayTitle?(state: T): string;
  customTitle(state: T): string | undefined;
  description(state: T, team?: Team, flowType?: FlowType): string;
  /**
   * Optional rich description rendered as a ReactNode inside the canvas
   * step card. When present, the node renders this instead of the plain
   * `description` string. Used by Issue compensation to render method
   * chips ("Exchange", "Store credit") under a "Compensation method:" label.
   */
  descriptionContent?(
    state: T,
    team?: Team,
    flowType?: FlowType,
  ): ReactNode | null;
  // React component names must start with an uppercase letter
  Details(props: StepTypeDetailsProps<T>): ReactNode | null;
  downstream(state: T): StepDownstream[];
  maxDownstream(state: T): number;
  fromModel(model: M, id: (id: ModelStepId) => StepId): T;
  Icon(props: any): ReactElement | null;
  iconColor?: FeaturedIconColor;
  additionalIcon?(
    state: T,
    team: Team | undefined,
    flowType: FlowType,
  ): ReactElement | null;
  layout(state: T): BlockLayout;
  start?(state: T): string | undefined;
  stepDeleted(state: T, step: StepId): T;
  updateConnection?(state: T, handleIndex: number, targetStepId: StepId): T;
  removeConnection?(state: T, targetStepId: StepId): T;
  toModel(state: T, id: (id: StepId) => ModelStepId): M;
  valid(state: T, flowType?: FlowType): boolean;
}

export type Step = TypedStep<any, any>;

export class TypedStep<T, M extends ModelStep> implements Step {
  constructor(
    readonly type: StepType<T, M>,
    public state: T,
  ) {}

  /**
   * The starting step ID
   * Undefined if this step never specifies a starting step.
   */
  get start(): StepId | null | undefined {
    return this.type.start ? this.type.start(this.state) || null : undefined;
  }

  details({
    stepSelect,
    setStep,
    setStepWithId,
    flowType,
    steps,
  }: {
    stepSelect: StepSelectFn;
    setStep: Dispatch<SetStateAction<Step | undefined>>;
    setStepWithId: (id: StepId, step: SetStateAction<Step | undefined>) => void;
    flowType: FlowType;
    steps: Map<StepId, Step>;
  }) {
    const setState: Dispatch<SetStateAction<T>> = (state) => {
      setStep(
        (step) =>
          step &&
          new TypedStep(
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
            (step as TypedStep<T, M>).type,
            state instanceof Function
              ? // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                state((step as TypedStep<T, M>).state)
              : state,
          ),
      );
    };

    const setStateWithId = (
      id: StepId,
      passedStep: SetStateAction<Step | undefined>,
      state: any,
    ) => {
      setStepWithId(
        id,
        passedStep &&
          new TypedStep(
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
            (passedStep as TypedStep<any, any>).type,
            state instanceof Function
              ? // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                state((passedStep as TypedStep<any, any>).state)
              : state,
          ),
      );
    };

    return (
      this.type && (
        <this.type.Details
          allSteps={steps}
          flowType={flowType}
          setState={setState}
          setStateWithId={setStateWithId}
          state={this.state}
          step={this}
          stepSelect={stepSelect}
        />
      )
    );
  }

  get Icon(): (props: { className?: string }) => ReactElement | null {
    return this.type.Icon;
  }

  get iconColor(): FeaturedIconColor | undefined {
    return this.type.iconColor;
  }

  setCustomTitle(
    title: string,
    setStep: Dispatch<SetStateAction<Step | undefined>>,
  ) {
    setStep(
      (step) =>
        step &&
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        new TypedStep((step as TypedStep<T, M>).type, {
          ...this.state,
          customTitle: title,
        }),
    );
  }

  description(team?: Team, flowType?: FlowType): string {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return this.type.description(this.state!, team, flowType);
  }

  descriptionContent(
    team?: Team,
    flowType?: FlowType,
  ): ReactNode | null | undefined {
    if (!this.type.descriptionContent) {
      return undefined;
    }
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return this.type.descriptionContent(this.state!, team, flowType);
  }

  additionalIcon(
    team: Team | undefined,
    flowType: FlowType,
  ): ReactElement | null {
    return this.type.additionalIcon
      ? this.type.additionalIcon(this.state, team, flowType)
      : null;
  }

  downstream(): StepDownstream[] {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return this.type.downstream(this.state!) || [];
  }

  maxDownstream(): number {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return this.type.maxDownstream?.(this.state!) || 0;
  }

  layout(): BlockLayout {
    return this.type.layout(this.state);
  }

  stepDeleted(step: StepId): Step {
    return new TypedStep(this.type, this.type.stepDeleted(this.state, step));
  }

  sidebarTitle(): string {
    const customTitle =
      this.type.customTitle && this.type.customTitle(this.state);
    return customTitle || this.type.title;
  }

  title(): string {
    const customTitle =
      this.type.customTitle && this.type.customTitle(this.state);
    if (customTitle) {
      return customTitle;
    }
    if (this.type.displayTitle) {
      return this.type.displayTitle(this.state);
    }
    return this.type.title;
  }

  ignore(): boolean {
    return this.type.ignore || false;
  }

  toModel(id: (id: StepId) => ModelStepId): M {
    return this.type.toModel(this.state, id);
  }

  valid(flowType: FlowType): boolean {
    return this.type.valid(this.state, flowType);
  }

  information(): string {
    return this.type.information || "";
  }
}

export interface StepDownstream {
  id: StepId | undefined;
  label?: string;
}

export const getStepResetPointText = (
  step: Step,
  returnType: ReturnTypeEnum,
) => {
  const stepTitle = getStepTitle(step, returnType);

  return `${stepTitle}${step.description() ? `: ${step.description()}` : ""}`;
};

export const getStepTitle = (step: Step, returnType: ReturnTypeEnum) => {
  if (step.title()) {
    return step.title();
  }

  // If there is no title, it is a reason step
  switch (returnType) {
    case ReturnTypeEnum.CLAIM:
      return t("Claim reason");
    case ReturnTypeEnum.WARRANTY:
      return t("Warranty reason");
    case ReturnTypeEnum.RETURN:
      return t("Return reason");
    case ReturnTypeEnum.RECYCLE:
      return t("Recycle reason");
    case ReturnTypeEnum.WITHDRAWAL:
      return t("Withdrawal reason");
    default:
      assertNever(returnType);
  }
};
